import {
  citationNamesMatchLoosely,
  citationPageNumbers,
  libraryPageHref,
  PAGE_CITATION_LIST_PATTERN,
} from '@/components/common/pageCitation';
import type {
  CitedSegment,
  CitedSource,
} from '@/components/ui/cited-answer-clean';
import type { LibraryReference } from '@/types/chat';

import { libraryDocumentHref } from './libraryDocuments';

/**
 * Connie's reply as Cited Answer wants it (VH3_CONNECT #549): sentences that name
 * the sources they rely on, and one source per cited (document, page). A citation
 * naming a document the turn never searched or read is left in the text as it is,
 * so a wrong page number never looks like a real source.
 */

export type CitedPage = { documentId: string; name: string; page: number };

export type CitedAnswerModel = {
  segments: CitedSegment[];
  sources: CitedSource[];
  /** The document and page behind each source id. */
  pages: Record<string, CitedPage>;
  /** Pages the turn read across every document, for the head line. */
  pagesRead: number;
};

export const sourceId = (documentId: string, page: number) =>
  `${documentId}:${page}`;

/** Only block constructs Cited Answer cannot show send a reply to Markdown instead. */
const BLOCK_MARKDOWN =
  /^\s{0,3}(?:#{1,6}\s|```|~~~|>|\|)|(?:^|[^\\])!?\[[^\]\n]*\]\([^)\n]*\)|<[a-z][^>]*>/im;

export const canRenderAsCitedAnswer = (content: string) =>
  !BLOCK_MARKDOWN.test(content);

const LIST_ITEM = /^\s{0,3}(?:[-*+]|\d{1,9}[.)])\s+/;
/** `[p. 5]` with no document name: Connie drops the name now and then. */
const BARE_PAGE_CITATION = /\[\s*p\.\s*(?<page>\d{1,5})\s*\]/g;
const SENTENCE_END = /(?<=[.!?])(?<!\bpp?\.)\s+/;
const MARKER = '\uE000';

const findReference = (references: readonly LibraryReference[], name: string) =>
  references.find(ref => ref.name && citationNamesMatchLoosely(name, ref.name));

/**
 * A citation with no document name can only mean one thing when the turn touched
 * exactly one named document. With more, it stays as text.
 */
const onlyReference = (references: readonly LibraryReference[]) => {
  const named = references.filter(ref => ref.name);
  return named.length === 1 ? named[0] : undefined;
};

/** Code, bold and italics marks go; the words stay. */
const stripInlineMarks = (text: string) =>
  text
    .replace(/`([^`]*)`/g, '$1')
    .replace(/(\*\*|__)(.+?)\1/g, '$2')
    .replace(/(^|[^\w*])\*([^*\n]+)\*(?!\w)/g, '$1$2')
    .replace(/(^|[^\w_])_([^_\n]+)_(?!\w)/g, '$1$2');

/**
 * Backslash escapes become the character. Runs after citations are resolved,
 * so an escaped `\[` never turns into a citation Connie wrote on purpose.
 */
const unescapeMarkdown = (text: string) => text.replace(/\\([[\]*_`])/g, '$1');

type Citation = { name: string; page: number; text: string };

/**
 * Pull every Page Citation out of a paragraph so sentence splitting never cuts
 * through `p. 63`, and so each sentence can list the ones it ends with.
 */
const protectCitations = (
  paragraph: string,
  only: LibraryReference | undefined
) => {
  const citations: Citation[] = [];
  let protectedText = paragraph.replace(
    PAGE_CITATION_LIST_PATTERN,
    (text, name: string, pages: string) => {
      // One citation per page named; only the first carries the text to put
      // back when the document is unknown.
      const numbers = citationPageNumbers(pages);
      if (!numbers.length) return text;
      return numbers
        .map((page, index) => {
          citations.push({ name, page, text: index === 0 ? text : '' });
          return `${MARKER}${citations.length - 1}${MARKER}`;
        })
        .join('');
    }
  );
  // A bare citation is set aside too, so "p. 5" never splits a sentence. With
  // no single document to resolve it, its empty name matches nothing and the
  // text comes back as it was.
  protectedText = protectedText.replace(
    BARE_PAGE_CITATION,
    (text, page: string) => {
      citations.push({ name: only?.name ?? '', page: Number(page), text });
      return `${MARKER}${citations.length - 1}${MARKER}`;
    }
  );
  return { protectedText, citations };
};

const MARKER_PATTERN = /\uE000(\d+)\uE000/g;
const LEADING_MARKERS = /^\s*(?:\uE000\d+\uE000\s*)+/;

export function buildCitedAnswer(
  content: string,
  references: readonly LibraryReference[],
  options: {
    /** Pages this turn read, when `references` also carry earlier turns'. */
    pagesRead?: number;
  } = {}
): CitedAnswerModel {
  const sources: CitedSource[] = [];
  const pages: Record<string, CitedPage> = {};
  const segments: CitedSegment[] = [];

  const sourceFor = (citation: Citation): string | null => {
    const ref = findReference(references, citation.name);
    if (!ref) return null;
    const id = sourceId(ref.documentId, citation.page);
    if (!pages[id]) {
      pages[id] = {
        documentId: ref.documentId,
        name: ref.name,
        page: citation.page,
      };
      // The chip shows `site`, so it names the page too: two pages of one
      // document must not look like one source twice.
      sources.push({
        id,
        title: `Page ${citation.page} of ${ref.name}`,
        site: `${ref.name} · p. ${citation.page}`,
        url: libraryDocumentHref(ref.documentId, citation.page),
      });
    }
    return id;
  };

  const paragraphs = content
    .replace(/\r\n?/g, '\n')
    .split(/\n[ \t]*\n+/)
    .flatMap(block =>
      block.split('\n').some(line => LIST_ITEM.test(line))
        ? block.split('\n').map(line => {
            const match = line.match(LIST_ITEM);
            return match
              ? `${/\d/.test(match[0]) ? match[0].trim() : '•'} ${line.slice(match[0].length)}`
              : line;
          })
        : [block.replace(/\n/g, ' ')]
    )
    .map(paragraph => paragraph.trim())
    .filter(Boolean);

  const merge = (into: string[], ids: string[]) => {
    for (const id of ids) if (!into.includes(id)) into.push(id);
  };

  const only = onlyReference(references);
  for (const paragraph of paragraphs) {
    const { protectedText, citations } = protectCitations(
      stripInlineMarks(paragraph),
      only
    );
    const pieces: { text: string; cite: string[] }[] = [];
    // Citations that stand before any sentence of the paragraph.
    let carried: string[] = [];
    for (const sentence of protectedText.split(SENTENCE_END)) {
      if (!sentence.trim()) continue;
      const cite: string[] = [];
      // Citations at the start of a sentence were written after the previous
      // sentence's full stop ("… thread. [Doc, p. 2] Next …"): they belong to
      // that sentence. Unknown ones stay as text.
      let kept = '';
      const body = sentence.replace(LEADING_MARKERS, run => {
        const leading: string[] = [];
        kept = run.replace(MARKER_PATTERN, (_match, n: string) => {
          const citation = citations[Number(n)];
          const id = sourceFor(citation);
          if (id) {
            if (!leading.includes(id)) leading.push(id);
            return '';
          }
          return citation.text;
        });
        const previous = pieces[pieces.length - 1];
        if (previous) merge(previous.cite, leading);
        else merge(carried, leading);
        return '';
      });
      const text = (kept + body).replace(
        MARKER_PATTERN,
        (_match, n: string) => {
          const citation = citations[Number(n)];
          const id = sourceFor(citation);
          if (id) {
            if (!cite.includes(id)) cite.push(id);
            return '';
          }
          return citation.text;
        }
      );
      const cleaned = unescapeMarkdown(text)
        .replace(/\s+([.,;:!?])/g, '$1')
        .replace(/[ \t]+/g, ' ')
        .trim();
      if (!cleaned) {
        // Connie often writes the citation after the full stop: "… thread. [Doc,
        // p. 2]". That marker belongs to the sentence before it.
        const previous = pieces[pieces.length - 1];
        if (previous) merge(previous.cite, cite);
        else merge(carried, cite);
        continue;
      }
      merge(cite, carried);
      carried = [];
      pieces.push({ text: cleaned, cite });
    }
    pieces.forEach((piece, index) => {
      segments.push({
        text: index < pieces.length - 1 ? `${piece.text} ` : piece.text,
        cite: piece.cite.length ? piece.cite : undefined,
        paragraph: index === 0,
      });
    });
  }

  const pagesRead =
    options.pagesRead ??
    references.reduce((total, ref) => total + ref.pagesRead.length, 0);
  return { segments, sources, pages, pagesRead };
}

/**
 * The Markdown fallback: each Page Citation that names a document the turn touched
 * becomes a link Markdown renders as a page marker. Used when the reply holds a
 * table, code or other blocks Cited Answer cannot show.
 */
export function linkLibraryCitations(
  content: string,
  references: readonly LibraryReference[]
): string {
  if (!references.some(ref => ref.name)) return content;
  const only = onlyReference(references);
  const linked = content.replace(
    PAGE_CITATION_LIST_PATTERN,
    (match, name: string, pages: string, offset: number, whole: string) => {
      const ref = findReference(references, name);
      const numbers = citationPageNumbers(pages);
      if (!ref || !numbers.length) return match;
      if (whole[offset + match.length] === '(') return match;
      // One marker per page: the first carries the name, the rest just the page.
      return numbers
        .map((page, index) =>
          index === 0
            ? `[\\[${name}, p. ${page}\\]](${libraryPageHref(ref.documentId, page)})`
            : `[\\[p. ${page}\\]](${libraryPageHref(ref.documentId, page)})`
        )
        .join(' ');
    }
  );
  if (!only) return linked;
  return linked.replace(
    BARE_PAGE_CITATION,
    (match, page: string, offset: number, whole: string) => {
      const pageNumber = Number(page);
      if (pageNumber < 1 || whole[offset + match.length] === '(') return match;
      return `[\\[p. ${pageNumber}\\]](${libraryPageHref(only.documentId, pageNumber)})`;
    }
  );
}
